import React, { useEffect, useState } from "react";
import "./dataPoints.css";
import DynamicKpiForm from "./DynamicKpiForm";
import Loader from "../../Loader";
import { useNavigate, useParams } from "react-router-dom";
import PageHeading from "../../common/PageHeading/PageHeading";
import OutlinedButton from "../../common/OutlinedButton";
import GradientButton from "../../common/GradientButton";
import usePutApi from "../../../utils/hooks/usePutApi";
import { reportEndpoints } from "../../../api/endpoints/report.endpoints";
import useFetch from "../../../utils/hooks/useFetch";
import usePostApi from "../../../utils/hooks/usePost";
import { useTaggingStore } from "./assignments/chatStore";
import { getSectors } from "../../../api/auth";
import { formSchemaApis } from "../../../api/formSchemaApis";
import ImportFromSdmModal from "./ImportFromSdmModal";
import ImportExcelModal from "./ImportExcelModal";

const Tab = ({ name, onClick, isActive }) => {
  return (
    <div
      className={`add-data-points_tab ${isActive ? "activeTab" : ""}`}
      onClick={onClick}
    >
      <h3 className={`add-data-points_tabName ${isActive ? "active" : ""}`}>
        {name}
      </h3>
    </div>
  );
};

const AddDataPoints = () => {
  const params = useParams();

  const {
    data: saveResponse,
    loading: savingProgress,
    error,
    put: saveProgress,
    status: saveStatus,
  } = usePutApi(reportEndpoints.saveProgress(params.id));

  const {
    data: generateResponse,
    error: generateError,
    loading: generateLoading,
    post: generateReport,
  } = usePostApi(reportEndpoints.generate(params.id));

  const { fetchUsers } = useTaggingStore();

  const { data: reportData, loading: reportLoading } = useFetch(reportEndpoints.getReport(params.id));
  const [resolvedSector, setResolvedSector] = useState(null);
  const [schemaTabs, setSchemaTabs] = useState([]);
  const [schemaTabsLoading, setSchemaTabsLoading] = useState(false);

  const [currentTab, setCurrentTab] = useState(0);
  const [sdmImportOpen, setSdmImportOpen] = useState(false);
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [importRefreshKey, setImportRefreshKey] = useState(0);
  const navigate = useNavigate();
  // const [environmentalKpiData, setEnvironmentalKpiData] = useState({
  //   scope1Emissions: "",
  //   scope2Emissions: "",
  //   totalWaterConsumption: "",
  // });

  // // Function to update the environmental KPI data
  // const updateEnvironmentalKpiData = (newData) => {
  //   setEnvironmentalKpiData((prevData) => ({
  //     ...prevData,
  //     ...newData,
  //   }));
  // };
  // console.log("environmentalKpiData", environmentalKpiData);
  const tabs = resolvedSector ? schemaTabs : [];

  const normalizeSection = (s) => String(s || "").trim().toLowerCase();
  const isGovernanceSection = (s) => {
    const n = normalizeSection(s);
    return n === "governance" || n === "governance & economics" || n === "economics";
  };
  const isStandardSection = (s) => {
    const n = normalizeSection(s);
    return n === "base" || n === "environment" || n === "social" || isGovernanceSection(s);
  };

  const buildTabsFromSchemas = (schemas) => {
    const hasBase = schemas.some((s) => normalizeSection(s.section) === "base");
    const hasEnv = schemas.some((s) => normalizeSection(s.section) === "environment");
    const hasSocial = schemas.some((s) => normalizeSection(s.section) === "social");
    const hasGov = schemas.some((s) => isGovernanceSection(s.section));
    const hasOther = schemas.some((s) => !isStandardSection(s.section));

    const nextTabs = [];
    const pushTab = (name, section) => {
      nextTabs.push({
        id: nextTabs.length,
        name,
        component: <DynamicKpiForm sector={resolvedSector} section={section} refreshKey={importRefreshKey} />,
      });
    };

    if (hasBase) pushTab("Base: Sector Specific KPI", "Base");
    if (hasEnv) pushTab("Environmental KPI", "Environment");
    if (hasSocial) pushTab("Social KPI", "Social");
    if (hasGov) pushTab("Governance & Economics KPI", "Governance");
    if (hasOther) pushTab("Other KPI", "Dynamic");

    return nextTabs;
  };

  function removeKeys(obj, keysToRemove) {
    if (obj === null || typeof obj !== "object") {
      return obj; // Return the value if it's not an object
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => removeKeys(item, keysToRemove));
    }

    const newObj = {};

    Object?.keys(obj)?.forEach((key) => {
      if (!keysToRemove?.includes(key)) {
        newObj[key] = removeKeys(obj[key], keysToRemove);
      }
    });

    return newObj;
  }

  function filterEmptyObjects(obj) {
    if (obj === null || typeof obj !== "object") {
      return obj; // Return if it's not an object
    }

    if (Array.isArray(obj)) {
      return obj
        .map((item) => filterEmptyObjects(item))
        .filter((item) => item !== undefined && item !== null && item !== "");
    }

    const newObj = {};

    Object.keys(obj).forEach((key) => {
      const value = obj[key];

      if (typeof value === "object" && value !== null) {
        const filteredValue = filterEmptyObjects(value);
        // Only include the key if the object or array has some valid values
        if (Object.keys(filteredValue).length > 0) {
          newObj[key] = filteredValue;
        }
      } else if (value !== undefined && value !== null && value !== "") {
        // Add non-null, non-empty, and non-undefined primitive values
        newObj[key] = value;
      }
    });

    return newObj;
  }

  const handleSaveProgress = async () => {
    const baseData = JSON.parse(localStorage.getItem("baseSectorKpiData"));
    const socialData = JSON.parse(localStorage.getItem("socialKpiData"));
    const environmentalData = JSON.parse(
      localStorage.getItem("environmentalKpiData")
    );
    const governanceData = JSON.parse(
      localStorage.getItem("governanceKpiData")
    );

    const finalObj = {};

    if (baseData !== null && baseData !== undefined) {
      finalObj.basicSectorSpecificKPI = baseData;
    }

    if (socialData !== null && socialData !== undefined) {
      finalObj.socialKPI = socialData;
    }

    if (environmentalData !== null && environmentalData !== undefined) {
      finalObj.environmentKPI = environmentalData;
    }

    if (governanceData !== null && governanceData !== undefined) {
      finalObj.governanceKPI = governanceData;
    }

    const keysRemoved = removeKeys(finalObj, ["id", "score", "unit"]);
    // console.log(keysRemoved);

    // console.log(filterEmptyObjects(keysRemoved));

    await saveProgress(filterEmptyObjects(keysRemoved))
      .then((res) => {
        alert(res?.data?.message);
      })
      .catch((err) => {
        alert(err?.message);
      });

    // if (saveStatus === 200) {
    //   alert("Progress saved");
    // } else {
    //   alert("Error occured");
    // }
  };

  const handleReportGeneration = async () => {
    try {
      const res = await generateReport({});
      if (res?.status >= 200 && res?.status < 300) {
        navigate(`/score-card/${params.id}`, { replace: true });
      }
    } catch (err) {
      const message = err?.message || "Please fill all the data points before generating the report";
      alert(message);
      console.log(err);
    }
  };

  useEffect(() => {
    fetchUsers(); // Fetch users once when component mounts
  }, []);

  useEffect(() => {
    let active = true;

    const loadSchemaTabs = async () => {
      if (!resolvedSector) {
        if (active) setSchemaTabs([]);
        return;
      }
      try {
        setSchemaTabsLoading(true);
        const schemasData = await formSchemaApis.getSchemasBySector(resolvedSector);
        const schemasArray = Array.isArray(schemasData)
          ? schemasData
          : (schemasData?.items || schemasData?.data || []);

        if (!active) return;
        const nextTabs = buildTabsFromSchemas(schemasArray);
        setSchemaTabs(nextTabs);
        setCurrentTab(0);
      } catch (e) {
        if (!active) return;
        setSchemaTabs([]);
      } finally {
        if (active) setSchemaTabsLoading(false);
      }
    };

    loadSchemaTabs();
    return () => {
      active = false;
    };
  }, [resolvedSector, importRefreshKey]);

  useEffect(() => {
    let isMounted = true;

    const resolveSectorName = async () => {
      const lookupSectorNameById = async (sectorId) => {
        try {
          const response = await getSectors();
          const sectors = response?.data || [];
          const matched = sectors.find(
            (item) => item?.id === sectorId || item?._id === sectorId
          );
          return matched?.name || null;
        } catch (error) {
          return null;
        }
      };

      const apiFormSchemaSector = reportData?.report?.formSchemaSector;
      if (apiFormSchemaSector) {
        if (isMounted) setResolvedSector(apiFormSchemaSector);
        return;
      }

      const sectorValue =
        reportData?.report?.sector ??
        reportData?.report?.organizationDetails?.organization?.sector ??
        null;

      if (!sectorValue) {
        if (isMounted) setResolvedSector(null);
        return;
      }

      const applyResolvedSector = (name) => {
        if (isMounted) {
          setResolvedSector(name);
        }
      };

      // Already a populated sector object.
      if (typeof sectorValue === "object" && sectorValue !== null) {
        if (sectorValue?.name) {
          applyResolvedSector(sectorValue.name);
          return;
        }
        const sectorId = sectorValue?.id || sectorValue?._id || null;
        if (sectorId) {
          const name = await lookupSectorNameById(String(sectorId));
          applyResolvedSector(name);
          return;
        }
      }

      // String can be either sector name or sector id.
      if (typeof sectorValue === "string") {
        const isObjectId = /^[a-fA-F0-9]{24}$/.test(sectorValue);
        if (!isObjectId) {
          applyResolvedSector(sectorValue);
          return;
        }

        const name = await lookupSectorNameById(sectorValue);
        applyResolvedSector(name);
        return;
      }

      if (isMounted) setResolvedSector(null);
    };

    resolveSectorName();
    return () => {
      isMounted = false;
    };
  }, [reportData]);

  return (
    <>
      <div className="add-data-points_container">
        {/* HEADING DIV */}
        {/* <div className="add-data-points_heading">
          <img
            style={{ cursor: "pointer" }}
            src={back}
            alt="Back"
            height={16}
            width={16}
            onClick={() => navigate("/")}
          />
          <h2>Add essential Data points</h2>
        </div> */}
        <div className="add-data-points_heading_container">
          <PageHeading
            onClick={() => navigate("/")}
            text={"Add essential data points"}
          />
        </div>

        {/* ALL TABS DIV  */}
        <div className="add-data-points_tabs_container">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              flexWrap: "wrap",
            }}
          >
            {tabs.map((item) => (
              <Tab
                key={item.id}
                name={item.name}
                onClick={() => setCurrentTab(item.id)}
                isActive={item.id === currentTab} // Ensure this comparison is correct
              />
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setExcelImportOpen(true)}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                border: "1px solid #C5D2CB",
                background: "#fff",
                borderRadius: "6px",
                padding: "8px 12px",
                fontSize: "12px",
                fontFamily: "Inter, sans-serif",
                color: "#2d5a45",
              }}
            >
              <span>Import Excel</span>
            </button>
            <button
              type="button"
              onClick={() => setSdmImportOpen(true)}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                border: "1px solid #C5D2CB",
                background: "#fff",
                borderRadius: "6px",
                padding: "8px 12px",
                fontSize: "12px",
                fontFamily: "Inter, sans-serif",
                color: "#2d5a45",
              }}
            >
              <span>Import from SDM</span>
            </button>
          </div>
        </div>

        <div className="add-data-points_divider"></div>

        {/* RENDER ACTIVE TAB COMPONENT */}
        <div className="add-data-points_content">
          <div className="add-data-points_table_container">
            {reportLoading || schemaTabsLoading ? (
              <Loader />
            ) : tabs.length > 0 ? (
              tabs.find((tab) => tab.id === currentTab)?.component
            ) : (
              <div style={{ padding: "20px", color: "#5b5b5b" }}>
                {resolvedSector
                  ? "No form schemas found for this sector yet. Please add schemas in the backend and try again."
                  : "No sector found for this report. Please ensure the organization has a sector assigned."}
              </div>
            )}
          </div>
        </div>
      </div>
      <ImportFromSdmModal
        open={sdmImportOpen}
        onClose={() => setSdmImportOpen(false)}
        reportId={params.id}
        onImported={(result) => {
          setImportRefreshKey((k) => k + 1);
          const msg = result?.message || "SDM data imported successfully.";
          alert(msg);
        }}
      />
      <ImportExcelModal
        open={excelImportOpen}
        onClose={() => setExcelImportOpen(false)}
        reportId={params.id}
        onImported={(result) => {
          setImportRefreshKey((k) => k + 1);
          const msg = result?.message || "Excel data imported successfully.";
          alert(msg);
        }}
      />
      {tabs.length > 0 && (
      <div className="kpi-table-btns_div">
        {currentTab !== 0 && currentTab !== tabs.length - 1 && (
          <button
            className="kpi-table-prev-btn"
            onClick={() => setCurrentTab((prev) => prev - 1)}
          >
            Previous
          </button>
        )}

        {currentTab !== tabs.length - 1 && (
          <button
            className="kpi-table-next-btn"
            onClick={() => setCurrentTab((prev) => prev + 1)}
          >
            Next
          </button>
        )}

        {currentTab === tabs.length - 1 && (
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {/* <OutlinedButton type="button" onClick={handleSaveProgress}>
              Save Progress
            </OutlinedButton> */}
            <GradientButton
              disable={generateLoading}
              onClick={() => {
                handleReportGeneration();
              }}
            >
              {generateLoading ? `Generating` : `Generate Report`}
            </GradientButton>
          </div>
        )}
      </div>
      )}
    </>
  );
};

export default AddDataPoints;
